"use strict";
/**
 * Phase 4 single-source schema manifest.
 *
 * The server runs the migrations listed here against Postgres via
 * `better-sqlite3` test paths and Postgres in prod. The client-side
 * SQLite-WASM mirror was removed in Phase 1.5 (see
 * `docs/architecture/SQLITE-WASM-EVALUATION.md`); `side: 'client'` is no
 * longer applied anywhere — entries kept only for historical migration ids
 * already recorded in `omni_system`.
 *
 * Keep the template literals in sync with the `.sql` files. The conformance
 * test suite and Phase 5 docs load the literals, not the SQL files.
 *
 * `side` determines which target applies a migration:
 *   - 'both'   — server only now (legacy: server + client)
 *   - 'server' — server only
 *   - 'client' — historical, no-op (no client runner registered)
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MIGRATIONS = void 0;
exports.migrationsForSide = migrationsForSide;
// Migration 005 lives in its own file so POC-3 (file storage) and POC-1
// (per-user isolation) could be authored in parallel without touching the
// same manifest file. See .research/action-plan-poc-flow.md.
const _005_files_1 = require("./005_files");
const _006_admin_fields_1 = require("./006_admin_fields");
const _007_file_blobs_1 = require("./007_file_blobs");
const _009_message_feedback_1 = require("./009_message_feedback");
const _010_feedback_drop_fk_1 = require("./010_feedback_drop_fk");
const _011_server_quick_actions_1 = require("./011_server_quick_actions");
const _012_user_prefs_1 = require("./012_user_prefs");
// ---------------------------------------------------------------------------
// 001 — identity placeholder (both sides)
// ---------------------------------------------------------------------------
const MIGRATION_001_SQL = `\
-- Migration 001: identity placeholder
-- Creates the omni_system migration tracking table only.
-- Actual domain tables (omni_users, omni_tenants, omni_communities, etc.) land in Phase 4 migration 002.

CREATE TABLE IF NOT EXISTS omni_system (
  migration_id TEXT PRIMARY KEY,
  applied_at   BIGINT  NOT NULL
);
`;
// ---------------------------------------------------------------------------
// 002 — Phase 4 core collab schema (both sides)
// ---------------------------------------------------------------------------
const MIGRATION_002_SQL = `\
-- Migration 002 (shared — both server and client): Phase 4 core collab schema.

CREATE TABLE IF NOT EXISTS omni_tenants (
  id          TEXT PRIMARY KEY,
  name        TEXT NOT NULL,
  created_at  BIGINT  NOT NULL,
  updated_at  BIGINT  NOT NULL
);
CREATE INDEX IF NOT EXISTS idx_omni_tenants_created_at
  ON omni_tenants(created_at);

CREATE TABLE IF NOT EXISTS omni_users (
  id                  TEXT PRIMARY KEY,
  tenant_id           TEXT NOT NULL REFERENCES omni_tenants(id),
  oid                 TEXT UNIQUE,
  display_name        TEXT,
  preferred_username  TEXT,
  created_at          BIGINT  NOT NULL,
  updated_at          BIGINT  NOT NULL
);
CREATE INDEX IF NOT EXISTS idx_omni_users_tenant_created
  ON omni_users(tenant_id, created_at);

CREATE TABLE IF NOT EXISTS omni_memberships (
  id          TEXT PRIMARY KEY,
  tenant_id   TEXT NOT NULL REFERENCES omni_tenants(id),
  user_id     TEXT NOT NULL REFERENCES omni_users(id),
  role        TEXT NOT NULL CHECK (role IN ('member','admin')),
  created_at  BIGINT  NOT NULL,
  updated_at  BIGINT  NOT NULL,
  UNIQUE (tenant_id, user_id)
);
CREATE INDEX IF NOT EXISTS idx_omni_memberships_tenant_created
  ON omni_memberships(tenant_id, created_at);

CREATE TABLE IF NOT EXISTS omni_communities (
  id          TEXT PRIMARY KEY,
  tenant_id   TEXT NOT NULL REFERENCES omni_tenants(id),
  name        TEXT NOT NULL,
  description TEXT,
  created_by  TEXT NOT NULL REFERENCES omni_users(id),
  created_at  BIGINT  NOT NULL,
  updated_at  BIGINT  NOT NULL
);
CREATE INDEX IF NOT EXISTS idx_omni_communities_tenant_created
  ON omni_communities(tenant_id, created_at);

CREATE TABLE IF NOT EXISTS omni_community_members (
  id            TEXT PRIMARY KEY,
  tenant_id     TEXT NOT NULL REFERENCES omni_tenants(id),
  community_id  TEXT NOT NULL REFERENCES omni_communities(id),
  user_id       TEXT NOT NULL REFERENCES omni_users(id),
  created_at    BIGINT  NOT NULL,
  UNIQUE (tenant_id, community_id, user_id)
);
CREATE INDEX IF NOT EXISTS idx_omni_community_members_tenant_created
  ON omni_community_members(tenant_id, created_at);
CREATE INDEX IF NOT EXISTS idx_omni_community_members_tenant_community
  ON omni_community_members(tenant_id, community_id);

CREATE TABLE IF NOT EXISTS omni_topics (
  id            TEXT PRIMARY KEY,
  tenant_id     TEXT NOT NULL REFERENCES omni_tenants(id),
  community_id  TEXT NOT NULL REFERENCES omni_communities(id),
  title         TEXT NOT NULL,
  created_by    TEXT NOT NULL REFERENCES omni_users(id),
  created_at    BIGINT  NOT NULL,
  updated_at    BIGINT  NOT NULL
);
CREATE INDEX IF NOT EXISTS idx_omni_topics_tenant_created
  ON omni_topics(tenant_id, created_at);
CREATE INDEX IF NOT EXISTS idx_omni_topics_tenant_community
  ON omni_topics(tenant_id, community_id);

CREATE TABLE IF NOT EXISTS omni_topic_participants (
  id         TEXT PRIMARY KEY,
  tenant_id  TEXT NOT NULL REFERENCES omni_tenants(id),
  topic_id   TEXT NOT NULL REFERENCES omni_topics(id),
  user_id    TEXT NOT NULL REFERENCES omni_users(id),
  created_at BIGINT  NOT NULL,
  UNIQUE (tenant_id, topic_id, user_id)
);
CREATE INDEX IF NOT EXISTS idx_omni_topic_participants_tenant_created
  ON omni_topic_participants(tenant_id, created_at);
CREATE INDEX IF NOT EXISTS idx_omni_topic_participants_tenant_topic
  ON omni_topic_participants(tenant_id, topic_id);

CREATE TABLE IF NOT EXISTS omni_threads (
  id                 TEXT PRIMARY KEY,
  tenant_id          TEXT NOT NULL REFERENCES omni_tenants(id),
  topic_id           TEXT NOT NULL REFERENCES omni_topics(id),
  parent_message_id  TEXT NULL,
  created_by         TEXT NOT NULL REFERENCES omni_users(id),
  created_at         BIGINT  NOT NULL,
  updated_at         BIGINT  NOT NULL
);
CREATE INDEX IF NOT EXISTS idx_omni_threads_tenant_created
  ON omni_threads(tenant_id, created_at);
CREATE INDEX IF NOT EXISTS idx_omni_threads_tenant_topic
  ON omni_threads(tenant_id, topic_id);

CREATE TABLE IF NOT EXISTS omni_messages (
  id                TEXT PRIMARY KEY,
  tenant_id         TEXT NOT NULL REFERENCES omni_tenants(id),
  topic_id          TEXT NOT NULL REFERENCES omni_topics(id),
  thread_id         TEXT NOT NULL REFERENCES omni_threads(id),
  author_type       TEXT NOT NULL CHECK (author_type IN ('user','agent')),
  author_user_id    TEXT NULL,
  author_agent_id   TEXT NULL,
  body              TEXT NOT NULL,
  created_at        BIGINT  NOT NULL,
  updated_at        BIGINT  NOT NULL,
  CHECK (
    (author_type = 'user'  AND author_user_id  IS NOT NULL AND author_agent_id IS NULL)
    OR
    (author_type = 'agent' AND author_user_id  IS NULL     AND author_agent_id IS NOT NULL)
  )
);
CREATE INDEX IF NOT EXISTS idx_omni_messages_tenant_created
  ON omni_messages(tenant_id, created_at);
CREATE INDEX IF NOT EXISTS idx_omni_messages_tenant_topic
  ON omni_messages(tenant_id, topic_id);
CREATE INDEX IF NOT EXISTS idx_omni_messages_tenant_thread
  ON omni_messages(tenant_id, thread_id);

CREATE TABLE IF NOT EXISTS omni_audit_log (
  id             TEXT PRIMARY KEY,
  tenant_id      TEXT NOT NULL REFERENCES omni_tenants(id),
  actor_user_id  TEXT NULL,
  action         TEXT NOT NULL,
  entity_type    TEXT NOT NULL,
  entity_id      TEXT NOT NULL,
  payload        TEXT,
  created_at     BIGINT  NOT NULL
);
CREATE INDEX IF NOT EXISTS idx_omni_audit_tenant_created
  ON omni_audit_log(tenant_id, created_at);
`;
// ---------------------------------------------------------------------------
// 003 — REMOVED (client-only outbound queue; Phase 1.5)
// ---------------------------------------------------------------------------
// The SQLite-WASM client mirror was removed in Phase 1.5; the outbound queue
// table was schema-only and never written to. See
// `docs/architecture/SQLITE-WASM-EVALUATION.md`. The id `003_client_outbound_queue`
// stays out of the MIGRATIONS array — server `omni_system` never recorded it
// (it was client-side only).
// ---------------------------------------------------------------------------
// 004 — per-user isolation (both sides)
// ---------------------------------------------------------------------------
const MIGRATION_004_SQL = `\
-- Migration 004 (both — server + client): per-user isolation.
-- Adds owner_user_id to user-owned tables so every read filters by it.

ALTER TABLE omni_threads     ADD COLUMN owner_user_id TEXT NOT NULL DEFAULT '';
ALTER TABLE omni_topics      ADD COLUMN owner_user_id TEXT NOT NULL DEFAULT '';
ALTER TABLE omni_communities ADD COLUMN owner_user_id TEXT NOT NULL DEFAULT '';
ALTER TABLE omni_messages    ADD COLUMN owner_user_id TEXT NOT NULL DEFAULT '';

CREATE INDEX IF NOT EXISTS idx_omni_messages_owner
  ON omni_messages(tenant_id, owner_user_id, thread_id, created_at);
CREATE INDEX IF NOT EXISTS idx_omni_threads_owner
  ON omni_threads(tenant_id, owner_user_id, created_at);
CREATE INDEX IF NOT EXISTS idx_omni_topics_owner
  ON omni_topics(tenant_id, owner_user_id, created_at);
CREATE INDEX IF NOT EXISTS idx_omni_communities_owner
  ON omni_communities(tenant_id, owner_user_id, created_at);
`;
/**
 * Ordered list of all Phase 3 + Phase 4 migrations.
 *
 * The runner applies them in order, skipping any whose `id` already exists in
 * `omni_system`. Runners filter by `side` to decide which to apply on their target.
 */
exports.MIGRATIONS = [
    {
        id: '001_identity',
        version: 1,
        name: 'identity placeholder (omni_system tracking table)',
        side: 'both',
        sql: MIGRATION_001_SQL,
    },
    {
        id: '002_core',
        version: 2,
        name: 'Phase 4 core collab schema',
        side: 'both',
        sql: MIGRATION_002_SQL,
    },
    // 003_client_outbound_queue removed in Phase 1.5 — client SQLite mirror gone
    {
        id: '004_per_user_isolation',
        version: 4,
        name: 'per-user isolation',
        side: 'both',
        sql: MIGRATION_004_SQL,
    },
    _005_files_1.MIGRATION_005,
    _006_admin_fields_1.MIGRATION_006,
    _007_file_blobs_1.MIGRATION_007,
    // 008_quick_actions removed in Phase 1.5 — client-only; server replacement is 011
    _009_message_feedback_1.MIGRATION_009_MESSAGE_FEEDBACK,
    _010_feedback_drop_fk_1.MIGRATION_010_FEEDBACK_DROP_FK,
    _011_server_quick_actions_1.MIGRATION_011_SERVER_QUICK_ACTIONS,
    _012_user_prefs_1.MIGRATION_012_USER_PREFS,
];
/**
 * Filter the manifest for a given target side. Always includes `both`.
 */
function migrationsForSide(side) {
    return exports.MIGRATIONS.filter((m) => m.side === 'both' || m.side === side);
}
//# sourceMappingURL=index.js.map